import {
  assignInnerRowGeometry,
  getVisibleNodeNames,
  type ViewQueryList,
  type ViewQueryNode,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { Result } from 'effect'

import { type DeepReadonly, toMutable } from '../../../rsheet/types/DeepReadonly'
import { replaceChildRecordNode } from '../../../rsheet/util/replaceChildRecordNode'
import { getInnerRecordNodePure, getRecordNodeFromRootNodePure } from '../../../state/nodeUtility'

export interface RelationChange {
  rowIndex: number
  innerRowIndex: number
  node: ViewQueryNode
  recordNode: ViewQueryRecordNode
}

/**
 * parentNode が tableNode に recordNode を持つようにする
 */
const assignRecordNode = (
  parentNode: DeepReadonly<ViewQueryRecordNode>,
  tableNodeName: string,
  recordNode: DeepReadonly<ViewQueryRecordNode>,
): DeepReadonly<ViewQueryRecordNode> => {
  const targetTableNode = parentNode.children.find((x) => x.nodeName === tableNodeName)
  if (targetTableNode === undefined) {
    // tableNodeがなければ作成する
    return {
      ...parentNode,
      children: [...parentNode.children, { nodeName: tableNodeName, children: [recordNode] }],
    }
  } else if (!targetTableNode.children.some((x) => x.id === recordNode.id)) {
    // 同じIDを持つレコードがなければ入れ替え
    return {
      ...parentNode,
      children: parentNode.children.map((x) =>
        x.nodeName === targetTableNode.nodeName ? { ...x, children: [recordNode] } : x,
      ),
    }
  }
  // tableNode があり、その tableNode が recordNode を持っている場合は、parentNode をそのまま返す
  return parentNode
}

/**
 * parentAttributes のラベル要素を childAttributes のラベル要素で更新した新しいオブジェクトを返す
 */
const updateParentAttributes = (
  parentAttributes: Record<string, unknown>,
  childAttributes: Record<string, unknown>,
): Record<string, unknown> => {
  const updatedAttributes = { ...parentAttributes }
  const childLabelEntries = Object.entries(childAttributes).filter(([k]) => k.endsWith('_label'))

  // ラベル要素があれば、親レコードのラベルを更新
  for (const [key, value] of childLabelEntries) {
    const baseKey = key.replace(/_label$/v, '')
    // eslint-disable-next-line unicorn/no-computed-property-existence-check
    if (key in parentAttributes || baseKey in parentAttributes) {
      updatedAttributes[key] = value
    }
  }
  return updatedAttributes
}

/**
 * 子レコードが変更されたことを、その親レコードの attributes に反映する
 *
 * @param records - ルートレコードノードの配列。この配列とその要素は変更されない。
 * @param relationChanges - レコードの更新情報（行インデックス、内部行インデックス、ノード情報）
 *   （普通は nodePath はセルのテーブルノードのパスなのに対し、） relationChange.node.path は `[...セルのテーブルノードのパス, テーブルノード名と参照項目の名をくっつけたもの]` であることに注意
 *   ルートレコードの参照セルの例: ['salesforce_opportunity', 'salesforce_opportunity_account_id'] (opportunityがルートテーブルノード、account_idが参照項目)
 *   子レコードの参照セルの例: ["salesforce_opportunity", "salesforce_account", "salesforce_account_owner_id"] (opportunityがルートテーブルノード、accountが子供のテーブルノード、owner_idが参照項目)
 *
 * @returns 更新されたルートレコードノードの配列と、全てのルートレコードノードの配列
 */
export function updateRecordNodeRelations(
  initialRecords: DeepReadonly<ViewQueryRecordNode[]>,
  relationChanges: DeepReadonly<RelationChange[]>,
  query: DeepReadonly<Pick<ViewQueryList, 'fields'>>,
): DeepReadonly<{
  updatedRootNodes: ViewQueryRecordNode[]
  allRootNodes: ViewQueryRecordNode[]
}> {
  const visibleNodeNames = getVisibleNodeNames(query)
  const records: Array<DeepReadonly<ViewQueryRecordNode>> = [...initialRecords] // 配列オブジェクトそのものを変更しないように浅いクローンをする
  const recordsWithUpdatedRelations: Array<DeepReadonly<ViewQueryRecordNode>> = []
  for (const relationChange of relationChanges) {
    const { rowIndex, innerRowIndex, node, recordNode } = relationChange
    // ルートのレコード(行)を取得
    const rootRecordNode = records[rowIndex]
    if (rootRecordNode === undefined) {
      continue
    }

    // 親レコードを取得
    const pathToParent = node.path.slice(0, -1)
    const parentRecordNodes = getRecordNodeFromRootNodePure(rootRecordNode, pathToParent)
    const parentRecordNode = Array.isArray(parentRecordNodes)
      ? getInnerRecordNodePure(parentRecordNodes, innerRowIndex)
      : parentRecordNodes
    if (parentRecordNode === undefined) {
      // childrenのnodeについて、emptyなセルの参照項目を直接編集すると起こりうるが、useEditModeする際にこのケースはonAddChildRecordしているはずなので問題ない
      // notifyBugsnag({ error: new Error(`parentRecordNode not found in changeRelation. ${node.path.join(',')}`) })
      continue
    }

    const parentNodeWithRecordNode = assignRecordNode(parentRecordNode, node.name, recordNode)
    const parentNodeWithUpdatedAttributes = {
      ...parentNodeWithRecordNode,
      attributes: updateParentAttributes(parentRecordNode.attributes, recordNode.attributes),
    }

    // 親ノードを置き換えた新しいルートノードを生成
    const newRootNodeResult =
      pathToParent.length === 1
        ? // 2層構造: parentRecordがrootRecordNodeなので、parentRecordをそのまま使う
          Result.succeed(parentNodeWithUpdatedAttributes)
        : // 3層以上の構造: replaceChildRecordNode で親ノードを置き換えた新しいルートノードを生成
          replaceChildRecordNode(rootRecordNode, pathToParent, parentNodeWithUpdatedAttributes)

    if (Result.isFailure(newRootNodeResult)) {
      // notifyBugsnag({ error: new Error(`Failed to replace parent node in updateRecordNodeRelations: ${newRootNodeResult.failure}`) })
      continue
    }
    const newRootNode = assignInnerRowGeometry(toMutable(newRootNodeResult.success), visibleNodeNames)
    recordsWithUpdatedRelations.push(newRootNode)
    records[rowIndex] = newRootNode
  }
  return {
    updatedRootNodes: recordsWithUpdatedRelations,
    allRootNodes: records,
  }
}
